import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import { debounceTime, filter, map, scan, tap } from 'rxjs/operators';
import { TableSelectionState } from '../domain/table-selection-state';
import { TableElementKey, TableSelectionAction } from '../domain/types';

@Directive({
  selector: '[psUiTableCheckboxColumn]',
})
export class TableCheckboxColumnDirective<T> {
  @Input()
  public set show(status: boolean) {
    this.showCommand.next(status);
  }

  @Input()
  public showSelectAll: boolean = true;

  @Input()
  public set selectBy(key: keyof T) {
    this.selectByCommand$.next(key);
  }

  @Input()
  public set selectedKeys(keys: TableElementKey<T>[]) {
    this.selectCommand$.next({ action: 'input', data: keys });
  }

  @Input()
  public set hideFor(keys: TableElementKey<T>[]) {
    this._hideFor$.next(keys);
  }

  @Input()
  public disable: boolean = false;

  @Input()
  public showInRow: boolean = false;

  @Input()
  public showIndeterminate: boolean = false;

  @Output()
  public selectedKeysChange = new EventEmitter<TableElementKey<T>[]>();

  public selected$!: Observable<TableSelectionState<T>>;
  public selectedBy$!: Observable<keyof T | undefined>;

  public get show$() {
    return this.showCommand.asObservable();
  }

  private readonly showCommand = new BehaviorSubject<boolean>(true);
  private readonly selectCommand$ = new BehaviorSubject<
    TableSelectionAction<T>
  >({ action: 'input', data: [] });
  private readonly selectByCommand$ = new BehaviorSubject<keyof T | undefined>(
    undefined
  );

  private readonly _hideFor$ = new BehaviorSubject<TableElementKey<T>[]>([]);

  constructor() {
    this.selectedBy$ = this.selectByCommand$.pipe();

    const state$ = this.selectCommand$.pipe(
      scan(
        (state, selection) => state.update(selection),
        TableSelectionState.none<T>()
      )
    );

    const action$ = state$.pipe(
      filter((state) => state.action !== 'input'),
      tap((state) => this.selectedKeysChange.emit(state.current()))
    );

    const input$ = state$.pipe(filter((state) => state.action === 'input'));
    const selection$ = merge(action$, input$);

    this.selected$ = combineLatest([
      this.selectedBy$,
      selection$,
      this._hideFor$,
    ]).pipe(
      debounceTime(100),
      map(([selectedBy, state, hideFor]) =>
        state.copyWith({ selectedBy, hideFor })
      )
    );
  }

  public select(selected: TableElementKey<T>): void;
  public select(selected: TableElementKey<T>[]): void;
  public select(selected: TableElementKey<T> | TableElementKey<T>[]): void {
    return Array.isArray(selected)
      ? this._selectMany(selected)
      : this._select(selected);
  }

  private _select(selected: TableElementKey<T>): void {
    const action: TableSelectionAction<T> = {
      data: [selected],
      action: 'selection',
    };

    this.selectCommand$.next(action);
  }

  private _selectMany(selected: TableElementKey<T>[]): void {
    const action: TableSelectionAction<T> = {
      data: selected,
      action: selected.length > 1 ? 'all' : 'none',
    };

    this.selectCommand$.next(action);
  }
}
